import React, { useState } from 'react';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Drawer from '@mui/material/Drawer';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

import Button from '../../../../../components/Button';
import AlertBanner from '../../../../../components/Candidate/AlertBanner';
import IconButton from '../../../../../components/IconButton/IconButton';
import Attendance from '../../../../../constants/attendance';
import DNRReasons from '../../../../../constants/DNR-reasons';
import UserRole from '../../../../../constants/user-role';
import selectUser from '../../../../../store/selectors/appSelector';
import theme from '../../../../../theme';
import LinearExpandMore from '../../../../corporations/corporationList/detail/icons/LinearExpandMore';

const headerText = 'Manage User'; // TODO support multiple language
const dnrDescription =
  'Applying a DNR for the employee means they will not be considered for future job opportunities';
const dnrReason = 'DNR Reason';
const favoriteAndDNRAlert = "A user can't be both a favorite and  DNR at the same time";

const heartStyle = { color: `${theme.components.detailView.palette.lightHeartColor}` };

const userNameStyle = {
  color: theme.components.detailView.palette.employeesNameColor,
  fontSize: `${theme.components.detailView.typography.manageUserName.fontSize}`,
  fontFamily: `${theme.components.detailView.typography.manageUserName.fontFamily}`,
  margin: theme.spacing(0, 0, 0, 1),
};

const sectionHeaderStyle = {
  fontColor: get(['components', 'detailView', 'palette', ' defaultTextColor'], theme),
  fontSize: get(['components', 'detailView', 'typography', 'sectionHeader', 'fontSize'], theme),
  fontFamily: get(['components', 'detailView', 'typography', 'sectionHeader', 'fontFamily'], theme),
  margin: theme.spacing(4, 0, 2, 0),
};

const dropdownBackgroundColor = get(
  ['components', 'selectDropdown', 'palette', 'grey', 'backgroundColor'],
  theme
);

const styleForSelect = {
  backgroundColor: dropdownBackgroundColor,
  borderRadius: '30px',
  minWidth: '130px',
  width: '80%',
  '& .Mui-focused': {
    borderRadius: '40px',
  },
};
const alertBannerTheme = get(['candidate', 'components', 'alertBanner'], theme);

const dnrTextStyle = {
  fontColor: get(['components', 'detailView', 'palette', '  dnrTextColor'], theme),
  fontSize: get(['components', 'detailView', 'typography', 'general', 'fontSize'], theme),
  fontFamily: get(['components', 'detailView', 'typography', 'general', 'fontFamily'], theme),
  margin: theme.spacing(0, 0, 2, 0),
};

const RootBox = styled(Box)(() => ({
  height: '100%',
  width: '100%',
  padding: theme.spacing(2, 8),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '20px',
}));

const HeaderBox = styled(Box)(() => ({
  width: '100%',
}));

const HeaderBoxTypography = styled(Box)(() => ({
  fontSize: `${theme.components.workflow.typography.defaultTitleFontSize}`,
  fontFamily: `${theme.components.workflow.typography.boldFont}`,
  color: `${theme.components.workflow.palette.font.default.transitionPageFontColor}`,
}));

const isDNR = (dnr) => Object.keys(dnr).length !== 0 && dnr.id !== DNRReasons.None;
const ManageUserDrawer = ({
  isOpen,
  onClose,
  candidateName,
  originalEmployerNote,
  handleOnSave,
  placementId,
  attendanceObj,
  fav,
}) => {
  const user = useSelector(selectUser);
  const [dnr, setDnr] = useState({});
  const [favorite, setFavorite] = useState(fav || false);
  const [attendance, setAttendance] = useState(attendanceObj || {});
  const [employerNote, setEmployerNote] = useState(originalEmployerNote || '');
  const employerNoteEditable = user.role === UserRole.EMPLOYER || user.role === UserRole.ADMIN;
  const isEmployer = user.role === UserRole.EMPLOYER;
  const isAdmin = user.role === UserRole.ADMIN;

  const attendanceOptions = [];

  Object.keys(Attendance).forEach((key) => {
    attendanceOptions.push({ id: key, label: Attendance[key] });
  });

  const dnrOptions = [];

  Object.keys(DNRReasons).forEach((key) => {
    dnrOptions.push({ id: key, label: DNRReasons[key] });
  });

  const handleUpdateRoster = () => {
    if (!isDNR(dnr) || !favorite) {
      const baseReturn = {
        id: placementId,
        DNR: isDNR(dnr) ? { reason: get(get('id', dnr), DNRReasons) } : { remove: true },
        fields: {
          attendance: get(get('id', attendance), Attendance),
        },
        favorite,
      };

      handleOnSave(
        ((isEmployer || isAdmin) && {
          ...baseReturn,
          fields: { ...baseReturn.fields, employerNote },
        }) ||
          baseReturn
      );
    }
  };

  const handleFavoriteClick = () => {
    setFavorite(!favorite);
  };

  const handleAttendanceChange = (e, value) => {
    setAttendance({ id: value.id, label: value.label });
  };

  const handleDNRChange = (e, value) => {
    setDnr({ id: value.id, label: value.label });
  };

  const handleEmployerNoteChange = (event) => {
    setEmployerNote(event.target.value);
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} PaperProps={{ sx: { width: '576px' } }}>
      <HeaderBox>
        <IconButton
          iconElement={<CloseIcon />}
          onClick={() => onClose()}
          size="small"
          sx={{ marginLeft: 0.5, marginTop: 0.5 }}
        />
      </HeaderBox>
      <RootBox>
        <HeaderBoxTypography>{`${headerText}`}</HeaderBoxTypography>
        <Box>
          <Box>
            {isDNR(dnr) && favorite && (
              <AlertBanner
                LeftIcon={WarningAmberIcon}
                message={favoriteAndDNRAlert}
                backgroundColor={get(['backgroundColor'], alertBannerTheme)}
                messageSx={{
                  color: get(['fontColor'], alertBannerTheme),
                  fontFamily: get(['font'], alertBannerTheme),
                }}
              />
            )}
          </Box>
          <Box display="flex">
            <Box onClick={handleFavoriteClick} sx={{ justifyContent: 'flex-end' }}>
              {favorite ? <FavoriteIcon sx={heartStyle} /> : <FavoriteBorderIcon sx={heartStyle} />}
            </Box>
            <Typography sx={userNameStyle}>{candidateName}</Typography>
          </Box>
          <Box>
            <Typography sx={sectionHeaderStyle}> Attendance Log</Typography>
            <Autocomplete
              isOptionEqualToValue={(option, val) => {
                if (!val.id) return true;
                return option.id === val.id;
              }}
              disableClearable
              size="small"
              id="Job-Roster-Attendance"
              options={attendanceOptions}
              sx={styleForSelect}
              value={attendance}
              popupIcon={<LinearExpandMore />}
              getOptionLabel={(option) => (option.label ? option.label : '')}
              PaperComponent={({ children }) => (
                <Paper sx={{ backgroundColor: dropdownBackgroundColor }}>{children}</Paper>
              )}
              onChange={handleAttendanceChange}
              renderInput={(inputParams) => <TextField {...inputParams} />}
            />
          </Box>
          <Box>
            <Typography sx={sectionHeaderStyle}> Employer Note</Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={employerNote}
              onChange={handleEmployerNoteChange}
              multiline
              placeholder="Enter text here..."
              rows={3}
              InputProps={{
                readOnly: !employerNoteEditable,
              }}
              sx={{
                backgroundColor: get(['select', 'main'], theme),
                borderRadius: '40px',
                '& .MuiInputBase-root': {
                  color: `${theme.palette.secondary.light}`,
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: `${theme.textField.background.grey}`,
                  borderRadius: '15px',
                  fontFamily: 'Barlow-600',
                  fontSize: '16px',
                  paddingTop: '5px',
                  paddingBottom: '5px',
                },
              }}
            />
          </Box>
          <Box>
            <Typography sx={sectionHeaderStyle}> DNR</Typography>
            <Typography sx={dnrTextStyle}>{dnrDescription}</Typography>
            <Typography sx={dnrTextStyle}>{dnrReason}</Typography>
            <Autocomplete
              isOptionEqualToValue={(option, val) => {
                if (!val.id) return true;
                return option.id === val.id;
              }}
              disableClearable
              size="small"
              id="Job-Roster-DNR"
              options={dnrOptions}
              sx={styleForSelect}
              value={dnr}
              popupIcon={<LinearExpandMore />}
              getOptionLabel={(option) => (option.label ? option.label : '')}
              PaperComponent={({ children }) => (
                <Paper
                  sx={{
                    backgroundColor: dropdownBackgroundColor,
                    fontFamily: get(
                      ['components', 'selectDropdown', 'typography', 'fontFamily'],
                      theme
                    ),
                    fontSize: get(['components', 'selectDropdown', 'typography', 'fonSize'], theme),
                  }}
                >
                  {children}
                </Paper>
              )}
              onChange={handleDNRChange}
              renderInput={(inputParams) => <TextField {...inputParams} />}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            alignSelf: 'flex-end',
          }}
        >
          <Box>
            <Button
              onClick={onClose}
              type="outlined"
              startIcon={<ArrowBackIcon sx={{ color: theme.button.palette.secondary }} />}
            >
              Go Back
            </Button>
          </Box>

          <Box>
            {' '}
            <Button
              disabled={isDNR(dnr) && favorite}
              onClick={handleUpdateRoster}
              sx={{ marginLeft: '5px' }}
              text="Update Employee"
            />
          </Box>
        </Box>
      </RootBox>
    </Drawer>
  );
};

ManageUserDrawer.propTypes = {
  selectedPlacementId: PropTypes.string,
  candidateName: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  totalShifts: PropTypes.string,
  lastShiftDate: PropTypes.string,
  accordionType: PropTypes.string,
  empId: PropTypes.string,
  handleOnSave: PropTypes.func,
  placementId: PropTypes.string,
  mode: PropTypes.string,
  attendanceObj: PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
  canceled: PropTypes.bool,
  fav: PropTypes.bool,
  name: PropTypes.string,
  originalRating: PropTypes.number,
  reason: PropTypes.string,
  originalDnrBl: PropTypes.bool,
  originalEmployerNote: PropTypes.string,
  isRecruiterPlacementBlocked: PropTypes.bool,
  currUserRole: PropTypes.string,
};

export default ManageUserDrawer;
